// Everything the pages share, live from Firestore: the signed-in user, the
// business, invoices, clients, the plan and the remote config. Started once
// at the top of the app; pages read it with useAppData().
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { activeBusinessOf, type Business, ownedBy, ownerOf } from '@/core/business';
import type { EveryBusiness } from '@/app/actions';
import type { Client } from '@/core/client';
import type { Invoice } from '@/core/invoice';
import type { SavedItem } from '@/core/savedItem';
import { FREE_PLAN, isProOn, type Plan } from '@/core/plan';
import { publicInvoiceJson } from '@/core/publicInvoice';
import { type AuthUser, ensureSignedIn, watchUser } from '@/data/authService';
import { type AppConfig, DEFAULT_FLAGS, loadConfig } from '@/data/config';
import { getCachedBusinesses, onWriteRefused, publishPublicInvoice, removePublicInvoice, watchBusinesses, watchClients, watchInvoices, watchItems, watchPlan } from '@/data/store';
import { identify, startAnalytics, track } from '@/data/analytics';
import { isProduction } from '@/data/firebase';
import { readPreference, writePreference } from '@/data/preferences';
import { t } from '@/l10n/strings';
import { useToast } from '@/ui/Toasts';

export type Phase = 'starting' | 'maintenance' | 'signInFailed' | 'ready';

export interface AppData {
  phase: Phase;
  config: AppConfig;
  user: AuthUser | null;
  /** The business in use. Undefined while loading; null when there is no business yet. */
  business: Business | null | undefined;
  /** Every business, oldest first. Undefined while loading. */
  businesses: Business[] | undefined;
  /** Every business's records, for merging and deleting a business. Undefined while loading. */
  every: EveryBusiness | undefined;
  /** Puts [id] in use in this browser: invoices, clients and items follow it. */
  setActiveBusiness: (id: string) => void;
  /** Undefined while loading. Newest first. */
  invoices: Invoice[] | undefined;
  /** Invoices with changes not yet on the server. */
  unsynced: Set<string>;
  clients: Client[] | undefined;
  /** Saved items ("My items"), most recently changed first. Undefined while loading. */
  items: SavedItem[] | undefined;
  plan: Plan;
  isPro: boolean;
  /** Pro features are open: Pro hasn't launched (all free) or this user has it. */
  proUnlocked: boolean;
  /** Pro is on sale and this user doesn't have it, so it can be offered. */
  offersPro: boolean;
  online: boolean;
}

const AppDataContext = createContext<AppData | null>(null);

export function useAppData(): AppData {
  const data = useContext(AppDataContext);
  if (!data) throw new Error('useAppData outside AppDataProvider');
  return data;
}

/** The signed-in user's id, for pages that only show once signed in. */
export function useUid(): string {
  const { user } = useAppData();
  if (!user) throw new Error('Not signed in');
  return user.uid;
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>('starting');
  const [config, setConfig] = useState<AppConfig>({ flags: DEFAULT_FLAGS, maintenanceMode: false, maintenanceMessage: '' });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [businesses, setBusinesses] = useState<Business[] | undefined>(undefined);
  const [chosenId, setChosenId] = useState<string | null>(() => readPreference('active_business'));
  const [allInvoices, setInvoices] = useState<Invoice[] | undefined>(undefined);
  const [unsynced, setUnsynced] = useState<Set<string>>(new Set());
  const [allClients, setClients] = useState<Client[] | undefined>(undefined);
  const [allItems, setItems] = useState<SavedItem[] | undefined>(undefined);

  // The business in use, and only its invoices, clients and saved items
  // (older records without a business belong to the first one).
  const business = useMemo(() => (businesses === undefined ? undefined : activeBusinessOf(businesses, chosenId)), [businesses, chosenId]);
  const primaryId = businesses?.[0]?.id ?? null;
  const invoices = useMemo(() => allInvoices?.filter((i) => ownedBy(i.businessId, business ?? null, primaryId)), [allInvoices, business, primaryId]);
  const clients = useMemo(() => allClients?.filter((c) => ownedBy(c.businessId, business ?? null, primaryId)), [allClients, business, primaryId]);
  const items = useMemo(() => allItems?.filter((i) => ownedBy(i.businessId, business ?? null, primaryId)), [allItems, business, primaryId]);
  const every = useMemo(
    () => (businesses && allInvoices && allClients && allItems ? { businesses, invoices: allInvoices, clients: allClients, items: allItems } : undefined),
    [businesses, allInvoices, allClients, allItems],
  );
  const setActiveBusiness = useCallback(
    (id: string) => {
      writePreference('active_business', id);
      setChosenId(id);
      track('business_switched', { business_count: businesses?.length ?? 1 });
    },
    [businesses],
  );
  const [plan, setPlan] = useState<Plan>(FREE_PLAN);
  const [online, setOnline] = useState(() => navigator.onLine);

  // Startup: the config and signing in (as a guest the first time) run
  // side by side, so a first visit doesn't wait for one before the other;
  // maintenance still holds everyone at the door.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const signingIn = ensureSignedIn();
      // Handled below; this stops an early failure counting as unhandled.
      signingIn.catch(() => {});
      const loaded = await loadConfig();
      if (cancelled) return;
      setConfig(loaded);
      void startAnalytics();
      if (loaded.maintenanceMode) {
        track('app_blocked', { reason: 'maintenance' });
        setPhase('maintenance');
        return;
      }
      try {
        const signedIn = await signingIn;
        if (cancelled) return;
        setUser(signedIn);
        setPhase('ready');
      } catch (error) {
        // Only possible on a first visit without internet.
        console.error('Guest sign-in failed', error);
        track('app_blocked', { reason: 'sign_in_failed' });
        if (!cancelled) setPhase('signInFailed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // After signing out there is nobody signed in: start afresh as a new guest.
  useEffect(
    () =>
      phase === 'ready'
        ? watchUser((next) => {
            setUser(next);
            if (!next) ensureSignedIn().catch((error) => console.error('Guest sign-in failed', error));
          })
        : undefined,
    [phase],
  );

  // The user's records, followed for as long as they're signed in.
  const uid = user?.uid;
  // Read when the records start, not followed: linking a guest to an
  // account keeps the same records and shouldn't restart them.
  const isGuest = useRef(false);
  isGuest.current = user?.isGuest ?? false;
  useEffect(() => {
    setBusinesses(undefined);
    setInvoices(undefined);
    setClients(undefined);
    setItems(undefined);
    setPlan(FREE_PLAN);
    if (!uid) return;
    const failed = (what: string) => (error: unknown) => console.error(`Could not load ${what}`, error);
    const stops = [
      watchBusinesses(uid, setBusinesses, failed('the businesses')),
      watchInvoices(
        uid,
        (all, pending) => {
          setInvoices(all);
          setUnsynced(pending);
        },
        failed('invoices'),
      ),
      watchClients(uid, setClients, failed('clients')),
      watchItems(uid, setItems, failed('saved items')),
      watchPlan(uid, setPlan),
    ];
    // A guest's records only ever live in this browser's copy, which is
    // read at once instead of waiting for the server to confirm a new
    // guest has no business yet; the server's answer still replaces it.
    if (isGuest.current) {
      void getCachedBusinesses(uid).then((cached) => setBusinesses((current) => (current === undefined ? cached : current)));
    }
    return () => stops.forEach((stop) => stop());
  }, [uid]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(
    () =>
      onWriteRefused(() => {
        track('write_refused');
        toast(t('syncChangeUndone'), { error: true });
      }),
    [toast],
  );

  const isPro = isProOn(plan);
  usePublicPages(uid, allInvoices, businesses, isPro);

  // Who is using the app, for slicing reports: the same user properties as
  // the phone app (TelemetrySession), never names or emails.
  const accountType = user ? (user.isGuest ? 'guest' : 'account') : null;
  const planName = !isPro ? 'free' : plan.isTrial ? 'trial' : 'pro';
  const currency = business?.currency ?? null;
  useEffect(() => {
    if (phase !== 'ready') return;
    identify(uid ?? null, { account_type: accountType, plan: planName, currency, app_flavor: isProduction ? 'prod' : 'staging' });
  }, [phase, uid, accountType, planName, currency]);

  // Tells the landing page (index.html) to offer a button straight into the
  // app, once there's a business in this browser.
  useEffect(() => {
    if (business === undefined) return;
    try {
      if (business) localStorage.setItem('paidlark.hasBusiness', '1');
      else localStorage.removeItem('paidlark.hasBusiness');
    } catch {
      // Storage is off; the landing page then simply shows each time.
    }
  }, [business]);

  const value = useMemo<AppData>(
    () => ({
      phase,
      config,
      user,
      business,
      businesses,
      every,
      setActiveBusiness,
      invoices,
      unsynced,
      clients,
      items,
      plan,
      isPro,
      proUnlocked: !config.flags.proLaunched || isPro,
      offersPro: config.flags.proLaunched && !isPro,
      online,
    }),
    [phase, config, user, business, businesses, every, setActiveBusiness, invoices, unsynced, clients, items, plan, isPro, online],
  );
  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/**
 * Keeps each shared invoice's public page (publicInvoices/{id}) in step
 * with the invoice and the business, as the phone app's PublicLinkService
 * does, and removes the page when its invoice is deleted.
 */
// Every business's invoices, each published with its own business: the
// pages stay up whichever business is in use.
function usePublicPages(uid: string | undefined, invoices: Invoice[] | undefined, businesses: Business[] | undefined, isPro: boolean) {
  const published = useRef(new Map<string, string>());
  const previous = useRef<Invoice[]>([]);

  useEffect(() => {
    published.current.clear();
    previous.current = [];
  }, [uid]);

  useEffect(() => {
    if (!uid || !invoices) return;
    const still = new Set(invoices.map((i) => i.publicLinkId));
    for (const old of previous.current) {
      if (old.publicLinkId && !still.has(old.publicLinkId)) {
        removePublicInvoice(old.publicLinkId);
        published.current.delete(old.publicLinkId);
      }
    }
    previous.current = invoices;
    for (const invoice of invoices) {
      const id = invoice.publicLinkId;
      const business = ownerOf(businesses ?? [], invoice.businessId);
      if (!id || !business) continue;
      const version = `${invoice.updatedAt}|${business.updatedAt}|${isPro}`;
      if (published.current.get(id) === version) continue;
      published.current.set(id, version);
      publishPublicInvoice(publicInvoiceJson({ id, ownerUid: uid, invoice, business, isPro })).catch((error) => {
        published.current.delete(id);
        console.error('Could not publish the invoice page', error);
      });
    }
  }, [uid, invoices, businesses, isPro]);
}
